import { useState } from 'react';
import { X } from 'react-bootstrap-icons';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';

import { IEventRequestModel } from '../../../interfaces/requests/IEventRequestModel';
import { createEvent } from '../../../http/eventAPI';

interface EventModalProps {
    show: boolean;
    handleClose: () => void;
    onEventCreated: () => void;
}

const AddEvent: React.FC<EventModalProps> = ({ show, handleClose, onEventCreated }) => {
    const [validated, setValidated] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [eventData, setEventData] = useState<IEventRequestModel>({
        title: '',
        description: '',
        location: '',
        image: new File([], ""),
        eventDatesAndTickets: [{ start_time: new Date(), ticketTypes: [{ type: '', price: 0, total_tickets: 0 }] }]
    });

    const formatDateTimeLocal = (date: Date | string) => {
        const d = new Date(date);

        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');

        return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

	const [dateInputs, setDateInputs] = useState<string[]>([formatDateTimeLocal(new Date())]);


    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setEventData({ ...eventData, [name]: value });
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setEventData({ ...eventData, image: e.target.files[0] });
        }
    };
    
	const handleEventDateBlur = (index: number, value: string) => {
		const newDate = new Date(value);

		if (Number.isNaN(newDate.getTime())) {
			return;
		}

		setEventData((prev) => ({
			...prev,
			eventDatesAndTickets: prev.eventDatesAndTickets.map((eventDate, i) =>
				i === index ? { ...eventDate, start_time: newDate } : eventDate
			),
		}));
	};

    const handleAddEventDate = () => {
        setEventData((prev) => ({
            ...prev,
            eventDatesAndTickets: [
                ...prev.eventDatesAndTickets,
                {
                    start_time: new Date(),
                    ticketTypes: [{ type: '', price: 0, total_tickets: 0 }]
                }
            ]
        }));

        setDateInputs((prev) => [...prev, formatDateTimeLocal(new Date())]);
    };

    const handleRemoveEventDate = (index: number) => {
        setEventData((prev) => ({
            ...prev,
            eventDatesAndTickets: prev.eventDatesAndTickets.filter((_, i) => i !== index)
        }));

        setDateInputs((prev) => prev.filter((_, i) => i !== index));
    };

    const handleTicketTypeChange = (dateIndex: number, ticketIndex: number, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        const updatedEventDates = eventData.eventDatesAndTickets.map((eventDate, i) => {
            if (i === dateIndex) {
                const updatedTicketTypes = eventDate.ticketTypes.map((ticket, j) =>
                    j === ticketIndex ? { ...ticket, [name]: value } : ticket
                );
                return { ...eventDate, ticketTypes: updatedTicketTypes };
            }
            return eventDate;
        });

        setEventData({ ...eventData, eventDatesAndTickets: updatedEventDates });
    };

    const handleAddTicketType = (dateIndex: number) => {
        const updatedEventDates = eventData.eventDatesAndTickets.map((eventDate, i) => {
            if (i === dateIndex) {
                return {
                    ...eventDate,
                    ticketTypes: [...eventDate.ticketTypes, { type: '', price: 0, total_tickets: 0 }]
                };
            }
            return eventDate;
        });

        setEventData({ ...eventData, eventDatesAndTickets: updatedEventDates });
    };

    const handleRemoveTicketType = (dateIndex: number, ticketIndex: number) => {
        const updatedEventDates = eventData.eventDatesAndTickets.map((eventDate, i) => {
            if (i === dateIndex) {
                const updatedTicketTypes = eventDate.ticketTypes.filter((_, j) => j !== ticketIndex);
                return { ...eventDate, ticketTypes: updatedTicketTypes };
            }
            return eventDate;
        });

        setEventData({ ...eventData, eventDatesAndTickets: updatedEventDates });
    };

    const handleCreateEvent = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setValidated(true);

        if (
            !eventData.title ||
            !eventData.description ||
            !eventData.location ||
            !eventData.image?.name ||
            !eventData.eventDatesAndTickets.length
        ) {
            setErrorMessage("Все поля обязательны для заполнения");
            return;
        }

        for (let eventDate of eventData.eventDatesAndTickets) {
            for (let ticket of eventDate.ticketTypes) {
                if (!ticket.type || ticket.price <= 0 || ticket.total_tickets <= 0) {
                    setErrorMessage("Заполните корректно все поля для типов билетов");
                    return;
                }
            }
        }

        const formData = new FormData();
        formData.append('title', eventData.title);
        formData.append('description', eventData.description);
        formData.append('location', eventData.location);

        if (eventData.image) {
            formData.append('image', eventData.image);
        }
        formData.append('eventDatesAndTickets', JSON.stringify(eventData.eventDatesAndTickets))

        createEvent(formData)
            .then((response) => {
                if (response === '200') {
                    onEventCreated();
                    handleClose();
                    setErrorMessage('');
                    setEventData({
                        title: '',
                        description: '',
                        location: '',
                        image: new File([], ""),
                        eventDatesAndTickets: [{ start_time: new Date(), ticketTypes: [{ type: '', price: 0, total_tickets: 0 }] }]
                    });
                    setDateInputs([formatDateTimeLocal(new Date())]);
                    setValidated(false);
                } else {
                    setErrorMessage(response);
                }
            })
            .catch((error) => {
                setErrorMessage('Произошла ошибка при создании события. Пожалуйста, попробуйте снова.');
                console.error('Ошибка при создании события:', error);
            });
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Новое событие</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
                <Form className='addevent_form' noValidate validated={validated}>
                    <Form.Group>
                        <Form.Label>Название</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Введите название"
                            name="title"
                            value={eventData.title}
                            onChange={handleInputChange}
                            required
                        />
                    </Form.Group>

                    <Form.Group>
                        <Form.Label>Описание</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Введите описание"
                            name="description"
                            value={eventData.description}
                            onChange={handleInputChange}
                            required
                        />
                    </Form.Group>

                    <Form.Group>
                        <Form.Label>Место проведения</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Введите местоположение"
                            name="location"
                            value={eventData.location}
                            onChange={handleInputChange}
                            required
                        />
                    </Form.Group>

                    <Form.Group>
                        <Form.Label>Изображение</Form.Label>
                        <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            required
                        />
                    </Form.Group>
                    <Form.Label>Даты проведения</Form.Label>
                    <Button variant="secondary" onClick={handleAddEventDate}>
                        Добавить дату
                    </Button>
                    {eventData.eventDatesAndTickets.map((eventDate, dateIndex) => (
                        <div key={dateIndex} className="event_unit">
                            <div>Мероприятие №{dateIndex +1}</div>
                            <div className='event_date_list'>
                                <Form.Group controlId={`formEventDate${dateIndex}`}>
                                    <Form.Control
                                        type="datetime-local"
                                        value={dateInputs[dateIndex] || ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setDateInputs((prev) =>
                                                prev.map((item, i) => (i === dateIndex ? value : item))
                                            );
                                        }}
                                        onBlur={(e) => handleEventDateBlur(dateIndex, e.target.value)}
                                        required
                                    />
                                </Form.Group>
                                <Button variant="danger" onClick={() => handleRemoveEventDate(dateIndex)}>
                                    <X />
                                </Button>
                            </div>
                            <div>
                                <Form.Label>Типы билетов</Form.Label>
                                <Button variant="secondary" onClick={() => handleAddTicketType(dateIndex)}>
                                    Добавить тип
                                </Button>
                            </div>
                            {eventDate.ticketTypes.map((ticketType, ticketIndex) => (
                                <div key={ticketIndex} className="ticket_type_container">
                                    <Button variant="danger" onClick={() => handleRemoveTicketType(dateIndex, ticketIndex)}>
                                        <X />
                                    </Button>
                                    <div className="ticket_type">
                                        <Form.Group>
                                            <Form.Label>Тип билета</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="type"
                                                value={ticketType.type}
                                                placeholder="Введите тип билета"
                                                onChange={(e) => handleTicketTypeChange(dateIndex, ticketIndex, e)}
                                                required
                                            />
                                        </Form.Group>
                                        <Form.Group>
                                            <Form.Label>Цена</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="price"
                                                value={ticketType.price}
                                                placeholder="Введите цену"
                                                onChange={(e) => handleTicketTypeChange(dateIndex, ticketIndex, e)}
                                                required
                                            />
                                        </Form.Group>
                                        <Form.Group>
                                            <Form.Label>Количество билетов</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="total_tickets"
                                                value={ticketType.total_tickets}
                                                placeholder="Введите количество билетов"
                                                onChange={(e) => handleTicketTypeChange(dateIndex, ticketIndex, e)}
                                                required
                                            />
                                        </Form.Group>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Закрыть
                </Button>
                <Button variant="success" onClick={handleCreateEvent}>
                    Создать событие
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddEvent;
